/**
 * Plan comparison cards with checkout buttons.
 *
 * Each plan has a base price ID (licensed) and an overage price ID (metered).
 * Configured via VITE_STRIPE_*_PRICE_ID and VITE_STRIPE_*_OVERAGE_PRICE_ID env vars.
 */

import { createCheckoutSession, createPortalSession } from "@/api/client";

interface Plan {
  id: string;
  name: string;
  price: string;
  priceId: string;
  overagePriceId: string;
  credits: string;
  overage: string;
  features: string[];
  highlighted?: boolean;
}

const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    priceId: "",
    overagePriceId: "",
    credits: "25 credits/mo",
    overage: "",
    features: [
      "Parcel lookup & search",
      "Tax calculations",
      "Basic property info",
      "3 active deals",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: "$99/mo",
    priceId: import.meta.env.VITE_STRIPE_STARTER_PRICE_ID || "",
    overagePriceId: import.meta.env.VITE_STRIPE_STARTER_PRICE_USAGE_ID || "",
    credits: "500 credits/mo",
    overage: "$0.25/credit overage",
    features: [
      "Everything in Free",
      "Property reports (tax, ownership, building)",
      "Comparable sales & market data",
      "Site screening (flood, environmental, zoning)",
      "Market intelligence & trends",
      "25 active deals",
    ],
    highlighted: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$349/mo",
    priceId: import.meta.env.VITE_STRIPE_PRO_PRICE_ID || "",
    overagePriceId: import.meta.env.VITE_STRIPE_PRO_PRICE_USAGE_ID || "",
    credits: "2,500 credits/mo",
    overage: "$0.15/credit overage",
    features: [
      "Everything in Starter",
      "Strategic intelligence (BATNA, disposition)",
      "Assessment appeal detection",
      "Tax arbitrage & TIF analysis",
      "Assemblage opportunities",
      "Bid optimization & distress screening",
      "Unlimited deals",
      "Priority support",
    ],
  },
];

interface PlanSelectorProps {
  currentTier: string;
  onClose?: () => void;
}

export function PlanSelector({ currentTier, onClose }: PlanSelectorProps) {
  const handleSelect = async (plan: Plan) => {
    if (plan.id === "free") return;
    if (plan.id === currentTier) {
      // Already on this plan — open portal to manage
      const url = await createPortalSession();
      if (url) window.location.href = url;
      return;
    }
    if (!plan.priceId) {
      alert("Plan not configured yet. Contact support.");
      return;
    }
    // Pass both base + overage price IDs for multi-line-item subscription
    const resp = await fetch("/api/billing/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        price_id: plan.priceId,
        overage_price_id: plan.overagePriceId || undefined,
        success_url: window.location.origin + "/?billing=success",
        cancel_url: window.location.origin + "/?billing=cancel",
      }),
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.url) window.location.href = data.url;
    }
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }}>
      <div style={{
        background: "var(--bg-primary, #111)",
        borderRadius: 12,
        padding: 24,
        maxWidth: 720,
        width: "90vw",
        border: "1px solid var(--border-primary, #2a2a2a)",
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--text-primary, #fff)" }}>
            Choose a Plan
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-secondary, #888)",
                cursor: "pointer",
                fontSize: 18,
              }}
            >
              &times;
            </button>
          )}
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
        }}>
          {PLANS.map((plan) => {
            const isCurrent = plan.id === currentTier;
            return (
              <div
                key={plan.id}
                style={{
                  border: plan.highlighted
                    ? "2px solid #3b82f6"
                    : "1px solid var(--border-primary, #2a2a2a)",
                  borderRadius: 10,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "var(--text-primary, #fff)",
                  }}>
                    {plan.name}
                  </div>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "var(--text-primary, #fff)",
                    marginTop: 4,
                  }}>
                    {plan.price}
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: "var(--text-secondary, #888)",
                    marginTop: 2,
                  }}>
                    {plan.credits}
                  </div>
                  {plan.overage && (
                    <div style={{
                      fontSize: 11,
                      color: "var(--text-tertiary, #666)",
                      marginTop: 2,
                    }}>
                      {plan.overage}
                    </div>
                  )}
                </div>
                <ul style={{
                  margin: 0,
                  padding: "0 0 0 16px",
                  fontSize: 13,
                  color: "var(--text-secondary, #ccc)",
                  flex: 1,
                }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{f}</li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelect(plan)}
                  disabled={plan.id === "free" && isCurrent}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: "none",
                    cursor: plan.id === "free" && isCurrent ? "default" : "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                    background: isCurrent
                      ? "var(--bg-tertiary, #333)"
                      : plan.highlighted
                        ? "#3b82f6"
                        : "var(--bg-secondary, #222)",
                    color: isCurrent
                      ? "var(--text-secondary, #888)"
                      : "#fff",
                  }}
                >
                  {isCurrent ? "Current Plan" : plan.id === "free" ? "Free" : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
